<script setup>
  import { watchThrottled } from '@vueuse/core'
  import { askLogin } from '@/utils/ask-login'

  const { user } = useUser()

  const { query } = useRoute()

  const { find } = useStrapi4()

  const route = useRoute()

  const employerTypes = reactive({
    ...(await useLazyAsyncData(
      'employer-types',
      async () => {
        const res = await find('employer-types', {
          fields: ['name', 'slug'],
          pagination: { limit: 100, start: 0, withCount: false },
          sort: { name: 'asc' }
        })
        return res.data
      },
      {
        default: () => []
      }
    ))
  })

  const regions = reactive({
    ...(await useLazyAsyncData(
      'regions',
      async () => {
        const res = await find('regions', {
          fields: ['name', 'slug'],
          pagination: { limit: 100, start: 0, withCount: false },
          sort: { name: 'asc' }
        })
        return res.data
      },
      {
        default: () => []
      }
    ))
  })

  const sectors = reactive({
    ...(await useLazyAsyncData(
      'sectors',
      async () => {
        const res = await find('sectors', {
          fields: ['name', 'slug'],
          pagination: { limit: 100, start: 0, withCount: false },
          sort: { name: 'asc' }
        })
        return res.data
      },
      {
        default: () => []
      }
    ))
  })

  const categories = reactive({
    ...(await useLazyAsyncData(
      'job-categories',
      async () => {
        const res = await find('job-categories', {
          fields: ['name', 'slug'],
          sort: { name: 'asc' }
        })
        return res.data
      },
      {
        default: () => []
      }
    ))
  })

  const filters = reactive({
    employerType: query.typeInstitution?.length ? employerTypes.data.filter(({ slug }) => query.typeInstitution.split(',').includes(slug)) : [],
    management: query.management !== undefined,
    region: query.localisation?.length ? regions.data.find(({ slug }) => query.localisation.split(',').includes(slug)) : undefined,
    search: query.recherche?.length ? query.recherche : '',
    sector: query.secteur?.length
      ? sectors.data.filter(({ slug }) =>
          query.secteur
            .split(',')
            .map(s => decodeURIComponent(s))
            .includes(slug)
        )
      : [],
    teleworkable: query.teletravail !== undefined,
    type: query.stageAlternance !== undefined,
    categories: query.categories?.length ? categories.data.filter(({ slug }) => query.categories.split(',').includes(slug)) : [],
    city: query.ville?.length ? query.ville : '',
    radius: query.rayon
  })

  const pagination = reactive({ limit: 7, start: 0, total: 0 })

  const jobParams = (filter = true) => {
    return {
      _q: filters.search.length ? filters.search.trim() : undefined,
      fields: ['moderated', 'slug', 'title', 'type', 'validatedAt', 'imported'],
      radius: filters.radius || undefined,
      city: filters.city || undefined,
      filters: filter
        ? {
            employer: filters.employerType.length
              ? {
                  type: { slug: { $in: filters.employerType.map(x => x.slug) } }
                }
              : undefined,
            locations: {
              ...(filters.region
                ? {
                    region: {
                      slug: { $in: filters.region.slug }
                    }
                  }
                : {}),
              ...(filters.city.length ? { city: { $containsi: filters.city } } : {})
            },
            management: filters.management || undefined,
            published: true,
            sectors: filters.sector.length
              ? {
                  slug: { $in: filters.sector.map(x => x.slug) }
                }
              : undefined,
            teleworkable: filters.teleworkable || undefined,
            type: filters.type ? 'internship' : undefined,
            categories: filters.categories.length
              ? {
                  slug: { $in: filters.categories.map(x => x.slug) }
                }
              : undefined
          }
        : {},
      populate: {
        employer: {
          fields: ['name'],
          populate: {
            cover: { fields: ['url'] },
            logo: { fields: ['url'] }
          }
        },
        header: {
          fields: ['title'],
          populate: { cover: { fields: ['url'] } }
        },
        locations: {
          fields: ['*'],
          populate: {
            region: { fields: ['name', 'slug'] }
          }
        },
        speaker: {
          fields: ['firstName', 'lastName'],
          populate: { photo: { fields: ['url'] } }
        },
        categories: {
          fields: ['name', 'slug']
        }
      },
      pagination: { limit: pagination.limit, start: pagination.start },
      sort: [{ imported: 'asc' }, { validatedAt: 'desc' }]
    }
  }

  const jobs = reactive({
    ...(await useLazyAsyncData(
      `jobs-${Date.now()}`,
      async () => {
        const params = jobParams()
        const res = await find('jobs', params)
        if (res.meta.pagination.start === 0) pagination.total = res.meta.pagination.total
        return [...jobs.data, ...res.data]
      },
      {
        default: () => [],
        server: false
      }
    ))
  })

  // Pour gérer les offres proches de la recherche au cas ou aucun job a été trouvé
  // On refait une recherche sans aucun filtre à part _q, donc la recherche de base
  const broaderJobs = reactive({
    ...(await useLazyAsyncData(
      `broaderJobs-${Date.now()}`,
      async () => {
        if (jobs.data.length) return []

        const params = jobParams(false)
        params.pagination = { limit: 100, start: 0 }
        params.sort = 'random'

        const res = await find('jobs', params)
        return res.data
      },
      {
        default: () => [],
        server: false,
        watch: [jobs]
      }
    ))
  })

  watchThrottled(
    filters,
    async () => {
      const tmp = {}
      if (filters.employerType.length) tmp.typeInstitution = filters.employerType.map(({ slug }) => slug).join(',')
      if (filters.management) tmp.management = null
      if (filters.region) tmp.localisation = filters.region.slug
      if (filters.search.length) tmp.recherche = filters.search
      if (filters.sector.length) tmp.secteur = filters.sector.map(({ slug }) => slug).join(',')
      if (filters.teleworkable) tmp.teletravail = null
      if (filters.type) tmp.stageAlternance = null
      if (filters.categories.length) tmp.categories = filters.categories.map(({ slug }) => slug).join(',')
      if (filters.city) tmp.ville = filters.city
      if (filters.radius) tmp.rayon = filters.radius
      await navigateTo({ query: tmp }, { replace: true })
      pagination.start = 0
      pagination.total = 0
      if (y.value > 250) y.value = 250
      jobs.data = []
      jobs.refresh()
    },
    { throttle: 500 }
  )

  const onLoadMore = () => {
    if (jobs.pending || jobs.error) return
    if (jobs.data.length < pagination.total) {
      pagination.start += pagination.limit
      jobs.refresh()
    }
  }

  const createAlert = ref(false)

  const { y } = useWindowScroll()
  const filtersElem = ref(null)
  const { height } = useElementSize(filtersElem)
  const { width } = useWindowSize()

  const isSearchBig = computed(() => {
    return y.value < 190
  })
</script>

<template>
  <Page class="bg-zinc-100" v-slot="{ page }" code="jobs">
    <section>
      <JobSearchForm
        class="shadow"
        ref="filtersElem"
        :filters="filters"
        :sectors="sectors"
        :regions="regions"
        :employer-types="employerTypes"
        :categories="categories"
      >
        <template v-slot:title>
          <h1 class="hidden font-special text-3xl font-bold sm:block sm:text-4xl md:text-5xl lg:pl-12">{{ page.title }}</h1>
          <div class="font-special text-xl font-bold sm:mt-4 sm:font-main sm:text-2xl sm:font-normal lg:pl-12">
            Trouvez un job <br class="max-md:hidden" />dans le secteur public
          </div>
        </template>
        <template v-slot:cta v-if="!isSearchBig && (!user || user.candidate)">
          <div class="order-last col-span-12 text-center md:order-3 md:col-span-3">
            <Button size="sm" @click="user ? (createAlert = true) : askLogin(route.path)">
              <Icon name="bell" type="filled" />
              <span class="ml-2">Créer une alerte</span>
            </Button>
          </div>
        </template>
      </JobSearchForm>
    </section>

    <div class="mx-auto max-w-screen-xl px-4 py-12" :style="{ marginTop: isSearchBig ? '0px' : `calc(14rem + ${width > 768 ? height : 0}px)` }">
      <InfiniteScroll v-if="!jobs.error && jobs.data.length" @loadMore="onLoadMore">
        <div class="mb-8 flex justify-between font-special text-xl font-bold">
          <div v-if="!jobs.pending">{{ pagination.total }} offre<template v-if="pagination.total > 1">s</template></div>
        </div>
        <div class="-m-4 flex flex-wrap items-center">
          <div class="w-full p-4" v-for="job in jobs.data">
            <BigCardJob :job="job" />
          </div>
          <Loading class="w-full" v-if="jobs.pending" size="2xl" />
        </div>
      </InfiniteScroll>
      <div class="min-h-[70vh]" v-else>
        <Loading v-if="jobs.pending" />
        <Error v-else-if="jobs.error" :retry="jobs.refresh" />
        <div v-else-if="!jobs.data.length">
          <div class="flex flex-col items-center">
            <div class="text-left">
              <p class="mb-1 text-xl font-bold">Nous sommes désolés, votre job de rêve n'est pas encore en ligne.</p>
              <p>
                - Essayez des termes plus généraux ou des synonymes<br />
                - Élargissez vos critères<br />
                - Créez une alerte pour de prochaines offres <br />
              </p>
            </div>
            <Button class="mt-4 self-center" size="sm" @click="user ? (createAlert.open.value = true) : askLogin(route.path)">
              <Icon name="bell" type="filled" />
              <span class="ml-2">Créer une alerte</span>
            </Button>
          </div>
          <div class="pt-12" v-if="broaderJobs.data.length">
            <p class="text-left text-lg font-bold">Les offres proches de vos critères :</p>
            <div class="-m-4 flex flex-wrap items-center pt-4">
              <div class="w-full p-4" v-for="job in broaderJobs.data">
                <BigCardJob :job="job" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <ClientOnly>
      <PagesJobsJobAlertDialog v-if="user?.candidate" :filters="filters" :open="createAlert" @close="createAlert = false" />
    </ClientOnly>
  </Page>
</template>
